import { defineStore, acceptHMRUpdate } from 'pinia'
import siteConfig from '@/config.json'
import axios from 'axios'
import sha1 from 'js-sha1'

import { usDateFormatToYYYYMMDD, dateToYYYYMMDD } from '@/helpers/datetime'
import { useClassStore } from '@/stores/ClassStore'
import { useInstructorStore } from '@/stores/InstructorStore'
import { useCartStore } from '@/stores/CartStore'

export const useUserStore = defineStore('UserStore', {
  state: () => {
    return {
      loggedIn: false,
      loggedOut: false,
      redirectTo: null,
      firstName: null,
      account: null,
      token: null,
      loading: true,
      bendLoggedIn: false,
      preAuthorizedEmails: [],
      accountItems: [],
      balance: 0,
      fetchedBalance: false,
      fetchingBalance: false,
      campaigns: [],
      fetchedMemberships: false,
      membershipsInHousehold: [],
      groupIds: [],
      registrationFees: [],
    }
  },

  getters: {
    isStaff() {
      return this.account && (!!this.account.staff || this.account?.email === 'kostas@artcentercanvas.com')
    },

    isActiveInstructor() {
      const instructorStore = useInstructorStore()

      if (!this.account) return false

      // return true // XXX
      return this.account.instructor && instructorStore.activeClassInstructorIds.includes(this.account.personId)
    },

    instructorClasses() {
      const classStore = useClassStore()

      if (!this.isActiveInstructor) {
        console.log('not an active instructor')
        return []
      }

      let instructorId = this.account.personId

      //XXX
      // instructorId = 328

      const classes = classStore.openClasses.filter(
        (cls) => cls.instructorId === instructorId || cls.instructor2Id === instructorId
      )
      console.log('instructorClasses', classes)
      return classes
    },

    wholeHouseholdMembership() {
      const mem = this.membershipsInHousehold.find((m) => {
        if (m.household === 1 && m.expire_date) {
          // console.warn("hmmmm")
          const expDate = m.expire_date.slice(0, 10)
          const currentDate = dateToYYYYMMDD(new Date())
          if (currentDate <= expDate) {
            return true
          }
        }
        return false
      })
      if (!mem) return null
      return mem
    },

    hasWholeHouseholdMembership() {
      return !!this.wholeHouseholdMembership
    },

    hasWholeHouseholdMembershipWithDiscount() {
      const mem = this.wholeHouseholdMembership
      return !!mem && mem.discounts
    },

    isActiveMember() {
      console.log('isActiveMember 1')
      if (this.hasWholeHouseholdMembership) {
        return true
      }

      // XXX Temp fix
      // if (siteConfig.key === 'potters' && this.account?.member) return true

      if (!this.account || !this.account.member) return false
      console.log('isActiveMember 2')

      let today = new Date()
      let todaysDateStandard = today.toISOString().split('T')[0]

      const expDate = usDateFormatToYYYYMMDD(this.account.memberExpirationDate, 2000, true)
      console.log('isActiveMember expDate', expDate, 'todaysDateStandard', todaysDateStandard)

      console.log('isActiveMember', expDate > todaysDateStandard)
      return expDate > todaysDateStandard
    },

    isActiveMemberWithDiscount() {
      // XXX hot fix
      if (siteConfig.key === 'potters' && this.account?.member) return true

      if (this.hasWholeHouseholdMembershipWithDiscount) return true
      if (!this.isActiveMember) return false

      if (!siteConfig.features.enhancedMembershipChecks) {
        return true
      }

      const membership = this.membershipsInHousehold.find((m) => m.member_id === this.account.personId)

      console.log('membership', membership)

      if (membership && membership.discounts === 1) {
        return true
      }
      return false
    },

    isPreAuthorized() {
      const ok = this.loggedIn && this.account && this.preAuthorizedEmails.includes(sha1(this.account.email))
      console.log('isPreAuthorized', ok)
      return ok
    },

    hasCompleteProfile() {
      let ok = true

      if (!this.account) {
        return ok
      }

      ok = ok && !!this.account.firstName
      ok = ok && !!this.account.lastName
      ok = ok && !!this.account.email

      if (ok && !siteConfig.features.genderIsOptionalInProfiles) {
        ok = ok && !!this.account.gender
      }

      ok = ok && !!this.account.dob
      ok = ok && !!this.account.homePhone

      if (siteConfig.features.onlyRequireZipCodeForAddress) {
        ok = ok && !!this.account.addrZip
      } else {
        ok = ok && !!this.account.addrStreet1
        ok = ok && !!this.account.addrCity
        ok = ok && !!this.account.addrState
        ok = ok && !!this.account.addrZip
      }

      if (ok && siteConfig.features.includeRaceFieldInProfiles && !siteConfig.features.raceIsOptionalInProfiles) {
        // console.log('ok', ok, 'this.account.race', this.account.race)
        ok = ok && !!this.account.race
      }
      if (ok && siteConfig.features.includeEthnicityFieldInProfiles) {
        ok = ok && !!this.account.ethnicity
      }
      if (ok && siteConfig.features.includePhotoReleaseInProfiles) {
        ok = ok && !!this.account.udef1
      }
      if (ok && siteConfig.features.includeMealPlanInProfiles) {
        ok = ok && !!this.account.udef2
      }
      if (ok && siteConfig.features.requireEmergencyContactInfo) {
        ok = ok && !!this.account.emergencyContact
      }

      return ok
    },
  },

  actions: {
    async flushCaches() {
      // XXX TODO Use existing method on userStore?
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/open?skipCache=1`)
        .then((response) => {
          console.log('classes cache updated!', response.data)
        })
        .catch((e) => {
          console.log('error flushing caches', e)
        })
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/programs?skipCache=1`)
        .then((response) => {
          console.log('programs cache updated!', response.data)
        })
        .catch((e) => {
          console.log('error flushing caches', e)
        })
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/instructors?skipCache=1`)
        .then((response) => {
          console.log('instructors cache updated!', response.data)
        })
        .catch((e) => {
          console.log('error flushing caches', e)
        })

      // axios.post(`${siteConfig.apiBaseUrl}/classes/flush`).then(response => {
      //   console.log('caches flushed!', response.data)
      // }).catch(e => {
      //   console.log('error flushing caches', e)
      // })
    },

    async fetchPreAuthorized() {
      try {
        const response = await axios.get(`/sites/${siteConfig.key}/hashed.json`)
        const hashed = response.data
        console.log('hashed', hashed)
        this.preAuthorizedEmails = hashed
        // console.log('sha1', sha1('kostas@artcentercanvas.com'))
      } catch (e) {
        console.log('error fetching waitlist', e)
      }
    },

    async fetchGroups() {
      if (this.loggedIn && this.account?.personId) {
        console.log('fetching groups')
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/groups-for-person?person_id=${this.account.personId}`
          )
          this.groupIds = response.data.results.map((group) => group.group_id)
          console.log('groupIds', this.groupIds)
        } catch (e) {
          console.log('error fetching groups', e)
        }
      }
    },

    async fetchRegistrationFees() {
      if (this.loggedIn && this.account?.personId) {
        console.log('fetching registration fees')
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/household-registration-fees?household_id=${this.account.householdId}`
          )
          this.registrationFees = response.data.results
          console.log('registrationFees', this.registrationFees)
        } catch (e) {
          console.log('error fetching groups', e)
        }
      }
    },

    async fetchMembershipsForHousehold() {
      if (!(siteConfig.modules.memberships && siteConfig.features.enhancedMembershipChecks)) {
        return
      }

      if (this.loggedIn && this.account.householdId) {
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/household-memberships?household_id=${this.account.householdId}`
          )
          this.membershipsInHousehold = response.data.results
          console.log('membershipsInHousehold', this.membershipsInHousehold)
          this.fetchedMemberships = true
        } catch (e) {
          console.log('error fetching memberships in household', e)
        }
      }
    },

    async fetchBalance() {
      console.log('fetchBalance')
      if (!this.account || !this.loggedIn) return
      if (this.fetchingBalance) return

      this.fetchingBalance = true

      axios
        .get(`${siteConfig.apiBaseUrl}/users/${this.account.personId}/balance`)
        .then((response) => {
          this.accountItems = response.data.items

          // XXX FIXME HACK Only show balance if there is just one item
          // if (this.accountItems.length <= 1) {
          this.balance = response.data.balance
          this.fetchedBalance = true
          this.fetchingBalance = false
          // }
        })
        .catch((error) => {
          console.log('fetchBalance error', error)
          this.fetchedBalance = true
          this.fetchingBalance = false
        })
    },

    async fetchCampaigns() {
      console.log('fetchCampaigns')

      axios
        .get(`${siteConfig.apiBaseUrl}/development/campaigns`)
        .then((response) => {
          const data = response.data

          if (data.ok) {
            this.campaigns = data.campaigns
            console.log('campaigns', this.campaigns)
          } else {
            console.warn('error loading campaigns')
          }
        })
        .catch((error) => {
          console.log('fetchCampaigns error', error)
        })
    },

    checkSession() {
      console.log('checking session')
      // XXX TODO Validate token
      let session
      try {
        const auth = localStorage.getItem(`${siteConfig.key}Auth`)
        if (auth) {
          // console.log('found auth', auth)
          session = JSON.parse(auth)
        }
      } catch (error) {
        console.log('error reading session data', error)
        this.loading = false
      }

      if (session) {
        console.log('session', session)
        this.firstName = session.name
        this.token = session.token

        axios
          .post(`${siteConfig.apiBaseUrl}/users/lookup`, { token: this.token })
          .then((response) => {
            this.account = response.data.account
            this.loggedIn = true
            this.fetchGroups()
          })
          .catch((error) => {
            console.log('checkSession user lookup error', error)

            // XXX maybe call logOut() and remove data from localStorage too??
            this.account = null
            this.loggedIn = false
            this.firstName = null
            this.token = null
          })
          .finally((data) => {
            console.log('finally', data)
            this.loading = false
            this.fetchMembershipsForHousehold()
            if (siteConfig.key === 'snowfarm') {
              this.fetchGroups()
              this.fetchRegistrationFees()
            }
          })
      }
    },

    logOut() {
      this.loggedIn = false
      this.token = null
      this.firstName = null
      this.account = null
      this.loggedOut = true
      this.accountItems = []
      this.balance = 0
      localStorage.removeItem(`${siteConfig.key}Auth`)
      const cartStore = useCartStore()
      cartStore.emptyCart()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
